import type { AxisSelection } from '@atlas/types'
import { type Article, ArticleType, type CreateArticleInput, type UpdateArticleInput } from '@/domain/article.types'

/**
 * The composer's editable state, and the only place the form's shape is mapped to
 * the API's. Both the create and the edit page go through these, so the two paths
 * cannot drift into sending different bodies for the same form.
 *
 * `status` is deliberately absent. The create schema rejects it and status moves
 * only through the transition endpoint, so a status field here would be a control
 * whose value is never sent.
 */
export interface ArticleFormState {
	title: string
	summary: string
	/** The body as HTML, as the editor produces it. */
	content: string
	articleType: ArticleType
	languageIso: string
	/** The article's images, in the author's order. */
	imageFileIds: string[]
	/** The classification tuple attached to the article (empty = nothing attached). */
	classification: AxisSelection
}

/**
 * The locale a new article is authored in. Fixed rather than offered: authoring a
 * translation is the translation flow's job, not the composer's, and every other
 * source-content form in the app opens on the same locale.
 */
export const DEFAULT_ARTICLE_LOCALE = 'en-us'

export const EMPTY_FORM: ArticleFormState = {
	title: '',
	summary: '',
	content: '',
	articleType: ArticleType.GENERAL,
	languageIso: DEFAULT_ARTICLE_LOCALE,
	imageFileIds: [],
	classification: {},
}

/** Hydrate the form from an article the API returned, including its resolved tuple. */
export function fromArticle(article: Article): ArticleFormState {
	return {
		title: article.title,
		summary: article.summary ?? '',
		content: article.content,
		articleType: article.articleType,
		languageIso: article.languageIso,
		imageFileIds: [...article.imageFileIds],
		classification: article.classification?.selection ?? {},
	}
}

/** Every `src` the body places an image at. */
function placedImageSources(content: string): string[] {
	const template = document.createElement('template')
	template.innerHTML = content
	return Array.from(template.content.querySelectorAll('img')).map((image) => image.getAttribute('src') ?? '')
}

/**
 * The article's images, split by whether the body still shows them.
 *
 * Deleting the `<img>` from the prose is how an image is removed, so the list the
 * API stores is DERIVED from the body when the article is saved rather than tracked
 * keystroke by keystroke. That timing is the point: backspace-then-undo has to cost
 * nothing, and an image must not be destroyed for an edit that is then cancelled —
 * until the save lands, the version a reader sees still shows it.
 *
 * An id is looked for inside each `src` because a file's key embeds its own id
 * (`<uploader>/<fileId>/content.<ext>`), which makes a placement self-identifying and
 * needs no request per image.
 *
 * `removedFileIds` is what the caller deletes once the save has succeeded.
 */
export function reconcileImageFileIds(form: ArticleFormState): { imageFileIds: string[]; removedFileIds: string[] } {
	const sources = placedImageSources(form.content)
	const placed = form.imageFileIds.filter((fileId) => sources.some((src) => src.includes(fileId)))

	// Inconclusive, not empty: the body holds images, yet not one of them names an id
	// this article owns. Rather than read that as "every image was deleted" and
	// destroy the lot, nothing is pruned — the only way here is a key layout that no
	// longer carries the file id, and losing an author's images must not be how that
	// gets noticed.
	if (placed.length === 0 && sources.length > 0) return { imageFileIds: [...form.imageFileIds], removedFileIds: [] }

	return { imageFileIds: placed, removedFileIds: form.imageFileIds.filter((fileId) => !placed.includes(fileId)) }
}

/**
 * Drop axes whose value list is empty, so an axis the author added and then
 * emptied is not sent as a key the API would read as "this axis, no values".
 */
function prunedClassification(classification: AxisSelection): AxisSelection {
	return Object.fromEntries(Object.entries(classification).filter(([, codes]) => (codes ?? []).length > 0))
}

/**
 * The create body. `slug` is omitted so the server derives it from the title;
 * `summary` is sent as `null` when blank because the column is nullable and an
 * empty string is not a summary.
 */
export function toCreateInput(form: ArticleFormState): CreateArticleInput {
	return {
		title: form.title.trim(),
		summary: form.summary.trim() || null,
		content: form.content,
		articleType: form.articleType,
		// Reconciled, not copied: an image whose placement the author deleted is no
		// longer one of the article's.
		imageFileIds: reconcileImageFileIds(form).imageFileIds,
		languageIso: form.languageIso,
		classification: prunedClassification(form.classification),
	}
}

/**
 * The update body. `slug` is left out on purpose: it is a stable handle, and
 * re-deriving it from an edited title would break any link already shared.
 *
 * `classification` is ALWAYS sent, which is what makes removing a tag possible —
 * the API replaces the tuple when the key is present and preserves it when it is
 * absent, so a composer that displays the tuple has to send it. `summary` is sent
 * as an explicit `null` when cleared, for the same reason: omitting it would
 * preserve a summary the author just deleted.
 */
export function toUpdateInput(form: ArticleFormState): UpdateArticleInput {
	return {
		title: form.title.trim(),
		summary: form.summary.trim() || null,
		content: form.content,
		articleType: form.articleType,
		imageFileIds: reconcileImageFileIds(form).imageFileIds,
		languageIso: form.languageIso,
		classification: prunedClassification(form.classification),
	}
}

/** Whether the form holds the minimum the API will accept: a title. */
export const canSubmit = (form: ArticleFormState): boolean => form.title.trim().length > 0
